<script setup>
import { computed } from 'vue'
import { useFloatingMenu } from '@/composables/useFloatingMenu'

defineOptions({
    name: 'DropdownMenu',
})

const props = defineProps({
    placement: {
        type: String,
        default: 'bottom-end',
    },
    offsetPx: {
        type: Number,
        default: 8,
    },
    matchWidth: {
        type: Boolean,
        default: false,
    },
    menuClass: {
        type: [String, Array, Object],
        default: 'w-52 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5',
    },
})

const {
    referenceRef,
    floatingRef,
    isOpen,
    floatingStyles,
    open,
    close,
    toggle,
} = useFloatingMenu({
    // Getters, not snapshotted values, so changing these props reactively
    // (e.g. flipping placement based on screen size) takes effect on the
    // next open rather than being frozen at mount time.
    placement: () => props.placement,
    offsetPx: () => props.offsetPx,
    matchWidth: () => props.matchWidth,
})

// Exposed to the trigger slot so the consumer binds it directly onto their
// real trigger element (e.g. `:ref="setReference"`). A wrapper element here
// would break positioning: `display: contents` (needed to stay layout-neutral)
// always reports a zero-sized getBoundingClientRect().
function setReference(el) {
    referenceRef.value = el
}

// Exposed to the trigger slot so consumers get correct aria-haspopup /
// aria-expanded on the real trigger element without re-deriving isOpen.
const ariaAttrs = computed(() => ({
    'aria-haspopup': 'true',
    'aria-expanded': String(isOpen.value),
}))

defineExpose({ open, close, toggle, isOpen })
</script>

<template>
    <slot
        name="trigger"
        :toggle="toggle"
        :open="open"
        :close="close"
        :is-open="isOpen"
        :set-reference="setReference"
        :ariaAttrs="ariaAttrs"
    />

    <Teleport to="body">
        <div
            v-if="isOpen"
            ref="floatingRef"
            role="menu"
            class="z-[9999] focus:outline-hidden"
            :class="menuClass"
            :style="floatingStyles"
        >
            <slot :close="close" />
        </div>
    </Teleport>
</template>
